import { assertType, requireNonNullable } from '@disy/lib/custom-error';
import { hashCode } from '@disy/lib/hash-code';
import { logger } from '@disy/lib/logging';
import { createIconButton } from '@disy/ui/button';
import { type MenuContent, setupMenuButton } from '@disy/ui/menu';
import { getSetting, getSettingOrSettingDefault } from '@disy/workbook.lib/settings';

import type { Action } from 'cadenza/action';
import { MenuAction, isMenuItemGroup, unpackMenuItemGroups } from 'cadenza/actions/menu-action';
import { getStore } from 'cadenza/store/cadenza-store';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { getDrillThroughDisplayModeSetting } from 'cadenza/workbook/drill-through/drill-through-utils';
import { CommonDataViewSettingsKey } from 'cadenza/workbook/model/common-data-view-settings-key.gen';
import { getSelectionViewId } from 'cadenza/workbook/selection-cart/selection-cart.state';
import type { SelectionRecordsDto } from 'cadenza/workbook/selection/model/selection-records-dto';
import { getDataViewById, getSelection } from 'cadenza/workbook/workbook-core-selectors.state';

/**
 * Create a map of values using the values hash code as a key.
 *
 * @param values - The values to create a map from
 * @param [relevantIndices] - Array of indices to extract from the row values in the given
 *   values array
 * @return - The values by their hash code as map
 */
export function getValuesByHash(values: unknown[][], relevantIndices?: number[]) {
  const valuesByHash = new Map<string, unknown[]>();
  for (const rowValues of values) {
    const extractedValues = relevantIndices ? extractValuesByIndex(rowValues, relevantIndices) : rowValues;
    const hash = toHashCode(extractedValues);
    valuesByHash.set(hash, extractedValues);
  }
  return valuesByHash;
}

/**
 * Extract values of an array specified by the given indices.
 *
 * @param array - The array to extract from
 * @param indices - Array of indices to extract the value of
 * @return An array containing only the values in the order as specified with the indices
 */
export function extractValuesByIndex(array: unknown[], indices: (number | undefined)[]) {
  const subset = [];
  const length = indices.length;
  for (let i = 0; i < length; i++) {
    const index = indices[i];
    subset.push(index != null ? array[index] : undefined);
  }
  return subset;
}

/**
 * Creates a hash code of the given values array for faster comparison
 *
 * @param values - The values array to create the hash code for
 * @return Hash code representing the values
 */
export function toHashCode(values: unknown[]) {
  return String(hashCode(JSON.stringify(values)));
}

export function getSelectionMap() {
  const viewId = getSelectionViewId(getStore().getState());
  return viewId && getMainMapAsync(viewId);
}

// Dynamic import to not bring in ol dependency on initial page load
async function getMainMapAsync(viewId: ViewId) {
  const { getMainMap } = await import('cadenza/map/custom/custom-map-utils');
  return getMainMap(viewId);
}

/**
 * Creates an icon button for the given action for usage in the selection cart.
 * Will create menu buttons for MenuAction instances. If the MenuAction contains only one action
 * it is inlined without menu.
 *
 * @param action - Action to create icon button for
 * @param separator - If true a separator will be added below groups.
 * @return returns a button that is used in the selection cart or undefined
 */
export function createActionIconButtonRespectingMenuActions(action: Action, separator = false) {
  if (action instanceof MenuAction) {
    const content = action.actionProvider();
    if (!content.length) {
      logger.debug(
        'At least one action child action is required for a button to be created - no button will be created.',
      );
      return;
    }

    if (content.length > 1 || isMenuItemGroup(content[0])) {
      // Menu button for more than one content actions
      const button = createActionIconButton(action);
      setupMenuButton(button, unpackMenuItemGroups(content, separator) as unknown as MenuContent);
      return button;
    }

    // Inline content action if only one available (We don't need an extra menu for that)
    return createActionIconButton(content[0]);
  }

  return createActionIconButton(action);
}

/**
 * Returns true if the drill-throughs should be displayed in the selection cart as a drop-down.
 */
export function isDrillThroughDropDown() {
  return isSelectionEnabledAndOneClickFilteringDisabled() && getDrillThroughDisplayMode() === 'DROPDOWN';
}

/**
 * Returns true if the drill-throughs should be displayed directly in the selection cart.
 */
export function isDrillThroughDirect() {
  return isSelectionEnabledAndOneClickFilteringDisabled() && getDrillThroughDisplayMode() === 'DIRECT';
}

export function isDisplayFilterAction() {
  const dataView = getDataViewFromSelection();
  return dataView ?
      getSettingOrSettingDefault(dataView, CommonDataViewSettingsKey.toPersistenceKey('DISPLAY_FILTER_ACTION'))
    : false;
}

export function isDisplayExportAction() {
  const dataView = getDataViewFromSelection();
  return dataView ?
      getSettingOrSettingDefault(dataView, CommonDataViewSettingsKey.toPersistenceKey('DISPLAY_EXPORT_ACTION'))
    : false;
}

export function getDataView(records: SelectionRecordsDto) {
  return getDataViewById(getStore().getState(), records.dataViewId);
}

/**
 * Create an icon button for the given action.
 *
 * @param action - Action to create icon button for
 * @return The created icon button
 */
function createActionIconButton(action: Action) {
  let styleClass: string[] = [];
  const actionStyleClass = action.styleClass;
  if (actionStyleClass) {
    // action.styleClass can be a string or an array of strings
    if (typeof actionStyleClass === 'string') {
      styleClass.push(actionStyleClass);
    } else {
      styleClass = styleClass.concat(actionStyleClass);
    }
  }

  assertType<string>(action.label, typeof action.label === 'string');
  return createIconButton(
    requireNonNullable(action.icon),
    action.tooltip ? `${action.label}\n${action.tooltip}` : action.label,
    {
      size: 's',
      styleClass,
      onclick: () => action.execute(),
      pressed: action.pressed,
      disabled: action.disabled,
    },
  );
}

function isSelectionEnabledAndOneClickFilteringDisabled() {
  const selectionEnabledKey = CommonDataViewSettingsKey.toPersistenceKey('SELECTION_ENABLED');
  const oneClickFilteringKey = CommonDataViewSettingsKey.toPersistenceKey('SELECTION_ONE_CLICK_FILTERING_ENABLED');
  const dataView = getDataViewFromSelection();
  return dataView ?
      getSetting(dataView, selectionEnabledKey, true) && !getSetting(dataView, oneClickFilteringKey, false)
    : false;
}

function getDataViewFromSelection() {
  const selection = getSelection(getStore().getState())!;
  const records = selection.records;
  return records ? getDataView(records) : undefined;
}

function getDrillThroughDisplayMode() {
  const dataView = getDataViewFromSelection();
  if (dataView) {
    const shouldDisplayDrillThrough = getSettingOrSettingDefault(
      dataView,
      CommonDataViewSettingsKey.toPersistenceKey('DISPLAY_DRILL_THROUGH'),
    );
    if (shouldDisplayDrillThrough) {
      return getDrillThroughDisplayModeSetting(dataView);
    }
  }
}
